/**
 * PassExperiencesSection
 * Reusable card grid of Pass offers (marketplace_visible) for the marketplace home.
 * Hidden entirely when no offers are returned.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Gift, MapPin, ArrowRight } from 'lucide-react';
import { passPublic } from '../../services/passApi';

const CATEGORY_LABEL = {
  restaurant: 'Restaurante',
  yacht: 'Yate',
  tour: 'Tour',
  nightlife: 'Antro',
  hotel_daypass: 'Day Pass',
  beach_club: 'Beach Club',
  jewelry: 'Joyería',
  vip_transport: 'Transporte VIP',
};

export default function PassExperiencesSection({ limit = 6 }) {
  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState([]);
  const [cities, setCities] = useState([]);
  const [activeCity, setActiveCity] = useState(null);

  useEffect(() => {
    let alive = true;
    passPublic
      .cities()
      .then((res) => alive && setCities(res.data.cities || []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    passPublic
      .marketplaceOffers({ limit, city: activeCity || undefined })
      .then((res) => alive && setOffers(res.data.offers || []))
      .catch(() => alive && setOffers([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [activeCity, limit]);

  // Hide section if nothing to show
  if (!loading && offers.length === 0 && !activeCity) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 mt-4">
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 rounded-2xl shadow-sm overflow-hidden">
        <div className="p-5 sm:p-7">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-teal-500/20 text-teal-200 text-[11px] font-semibold mb-2">
                <Sparkles className="w-3 h-3" /> Todo en Local Pass
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-white">Experiencias y promociones</h2>
              <p className="text-slate-300 text-sm">
                Restaurantes, yates, tours, antros, joyerías y más — recomendados por locales.
              </p>
            </div>
            <Link
              to="/experiencias"
              className="inline-flex items-center gap-1 text-teal-200 hover:text-white text-sm font-semibold"
            >
              Ver todas <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {cities.length > 1 && (
            <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-4 -mx-1 px-1">
              <CityChip label="Todas" active={!activeCity} onClick={() => setActiveCity(null)} />
              {cities.map((c) => (
                <CityChip key={c} label={c} active={activeCity === c} onClick={() => setActiveCity(c)} />
              ))}
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl h-40 animate-pulse" />
              ))}
            </div>
          ) : offers.length === 0 ? (
            <p className="text-slate-300 text-sm py-6 text-center">
              Sin experiencias activas en esta ciudad. Prueba otra.
            </p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {offers.map((o) => (
                <PassOfferCard key={o.id} offer={o} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function CityChip({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
        active
          ? 'bg-orange-500 text-white'
          : 'bg-white/10 text-slate-200 hover:bg-white/20'
      }`}
    >
      <MapPin className="w-3 h-3" /> {label}
    </button>
  );
}

function PassOfferCard({ offer }) {
  const benefit = offer.customer_benefits?.[0]?.name;
  return (
    <Link
      to={`/pass/oferta/${offer.slug}`}
      className="group bg-white rounded-xl overflow-hidden border border-white/10 hover:scale-[1.02] transition-transform"
    >
      <div className="aspect-[4/3] bg-slate-100 relative overflow-hidden">
        {offer.images?.[0] ? (
          <img src={offer.images[0]} alt={offer.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300">
            <Gift className="w-10 h-10" />
          </div>
        )}
        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-orange-500 text-white text-[10px] font-bold uppercase tracking-wide">
          {CATEGORY_LABEL[offer.category] || offer.category}
        </span>
      </div>
      <div className="p-3">
        <h3 className="text-sm font-semibold text-slate-900 leading-tight line-clamp-2">{offer.title}</h3>
        {offer.business_name && (
          <p className="text-[11px] text-slate-500 mt-0.5 truncate">{offer.business_name}</p>
        )}
        {benefit && (
          <p className="text-xs text-teal-600 font-medium mt-1.5 line-clamp-1">{benefit}</p>
        )}
      </div>
    </Link>
  );
}
